import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useMDSHotKey } from "@/mds/domains/hotkeys";
import {
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
} from "@mem-labs/common-editor";
import { MDSSessionContextValue } from "./mds-session-context.types";
import {
  MDSSessionArtifact,
  MDSSessionError,
  MDSSessionErrorKind,
} from "./slices/ui/mds-session-context.ui.types";
import { MDSSessionEventsAction } from "./slices/events/mds-session-context.events.types";
import { MDSSessionUIAction } from "./slices/ui/mds-session-context.ui.types";
import { MDSSessionAction } from "./mds-session-context.types";
import { mdsSessionReducer, createInitialState } from "./slices/mds-session-context.reducer";
import {
  IMDSSessionProps,
  SessionEvent,
  SessionStatus,
} from "@/mds/components/sessions/session.types";
import { SessionViewMode } from "@/mds/components/sessions/session.constants";
import { MDSSessionNotificationInfo } from "@/mds/components/sessions/session-notification/session-notification.types";
import { finalizeSessionItems } from "@/mds/components/sessions/session-context/mds-session-context.utils";
import { SessionFeature } from "@/mds/components/sessions/session.types";
import { isSessionFeatureEnabled } from "../session.utils";
import { SESSION_CLOSE_ARTIFACT_HOTKEY } from "@/domains/keyboard-shortcuts/shared-hotkeys";

const MDSSessionContext = createContext<MDSSessionContextValue | null>(null);

type MDSSessionProviderProps = Pick<
  IMDSSessionProps,
  "initialSessionEvents" | "initialSessionStatus" | "config" | "handlers"
> & {
  children: ReactNode;
};

export const MDSSessionProvider = forwardRef<MDSSessionContextValue, MDSSessionProviderProps>(
  (
    {
      children,
      initialSessionEvents,
      initialSessionStatus,
      config,
      handlers: { handleViewModeChanged, handleCountdownCompleted, handleRetry },
    },
    ref
  ) => {
    const [state, dispatch] = useReducer(
      mdsSessionReducer,
      createInitialState({
        sessionEvents: initialSessionEvents,
        sessionStatus: initialSessionStatus,
        sessionConfig: config,
      })
    );

    // Ref to store the combobox input instance
    const comboboxInputRef = useRef<MemCommonRichTextInputInstance | null>(null);

    const viewArtifact = useCallback(({ payload }: { payload: MDSSessionArtifact }) => {
      dispatch({
        type: MDSSessionUIAction.SetActiveArtifact,
        payload,
      });
    }, []);

    const closeArtifact = useCallback(() => {
      dispatch({
        type: MDSSessionUIAction.SetActiveArtifact,
        payload: null,
      });
    }, []);

    const upsertSessionEvents = useCallback((events: SessionEvent[]) => {
      dispatch({
        type: MDSSessionEventsAction.UpsertSessionEvents,
        payload: events,
      });
    }, []);

    const updateSessionStatus = useCallback(
      (status: SessionStatus) => {
        dispatch({
          type: MDSSessionEventsAction.UpdateSessionStatus,
          payload: status,
        });

        if (status === SessionStatus.Error && !state.ui.sessionError) {
          dispatch({
            type: MDSSessionUIAction.SetSessionError,
            payload: {
              message: "Something went wrong.",
              type: MDSSessionErrorKind.SessionEventError,
              canRetry: true,
            },
          });
        }
      },
      [state.ui.sessionError]
    );

    const minimizeSession = useCallback(() => {
      dispatch({
        type: MDSSessionUIAction.MinimizeSession,
      });
    }, []);

    const expandSession = useCallback(() => {
      const shouldBeInPreview = state.events.sessionStatus === SessionStatus.Initial;
      dispatch({ type: MDSSessionUIAction.SetPreviewMode, payload: shouldBeInPreview });
      dispatch({ type: MDSSessionUIAction.StopCountdown });
    }, [state.events.sessionStatus]);

    const expandFromNotification = useCallback(() => {
      dispatch({ type: MDSSessionUIAction.ExpandSession });
      dispatch({
        type: MDSSessionUIAction.SetNotificationState,
        payload: { isInNotificationState: false },
      });
      dispatch({ type: MDSSessionUIAction.StopCountdown });
    }, []);

    const expandFromPreview = useCallback(() => {
      dispatch({ type: MDSSessionUIAction.ExpandSession });
      dispatch({ type: MDSSessionUIAction.SetPreviewMode, payload: false });
      dispatch({ type: MDSSessionUIAction.StopCountdown });
    }, []);

    const toggleSessionItemExpandedState = useCallback(({ itemId }: { itemId: string }) => {
      dispatch({
        type: MDSSessionEventsAction.ToggleSessionItemExpandedState,
        payload: { itemId },
      });
    }, []);

    const startCountdown = useCallback((durationMs: number) => {
      dispatch({ type: MDSSessionUIAction.StartCountdown, payload: { durationMs } });
    }, []);

    const stopCountdown = useCallback(() => {
      dispatch({ type: MDSSessionUIAction.StopCountdown });
    }, []);

    const setInputFocused = useCallback((focused: boolean) => {
      dispatch({ type: MDSSessionUIAction.SetInputFocused, payload: focused });
    }, []);

    const setHovering = useCallback((hovering: boolean) => {
      dispatch({ type: MDSSessionUIAction.SetHovering, payload: hovering });
    }, []);

    const setNotificationState = useCallback(
      (options: {
        isInNotificationState: boolean;
        notificationInfo?: MDSSessionNotificationInfo;
      }) => {
        dispatch({ type: MDSSessionUIAction.SetNotificationState, payload: options });
      },
      []
    );

    const setSessionError = useCallback((error: MDSSessionError) => {
      dispatch({ type: MDSSessionUIAction.SetSessionError, payload: error });
    }, []);

    const clearSessionError = useCallback(() => {
      dispatch({ type: MDSSessionUIAction.ClearSessionError });
    }, []);

    const setPreviewMode = useCallback((isPreview: boolean) => {
      dispatch({ type: MDSSessionUIAction.SetPreviewMode, payload: isPreview });
    }, []);

    const publishNotification = useCallback(
      ({ notificationInfo }: { notificationInfo: MDSSessionNotificationInfo }) => {
        console.log("[Mem] Publishing notification:", state.ui.isMinimized, state.ui);
        dispatch({
          type: MDSSessionUIAction.SetNotificationState,
          payload: { isInNotificationState: state.ui.isMinimized, notificationInfo },
        });
        dispatch({ type: MDSSessionUIAction.StartCountdown, payload: { durationMs: 8000 } });
      },
      [state.ui]
    );

    const focusComboboxInput = useCallback(() => {
      if (!comboboxInputRef.current) {
        return;
      }

      comboboxInputRef.current.dispatchAction({
        kind: MemCommonRichTextInputActionKind.FocusEditor,
        payload: null,
      });
    }, []);

    const setComboboxInputRef = useCallback((ref: MemCommonRichTextInputInstance | null) => {
      comboboxInputRef.current = ref;
    }, []);

    const setCurrentSessionId = useCallback((sessionId: string | null) => {
      dispatch({
        type: MDSSessionAction.SetCurrentSessionId,
        payload: sessionId,
      });
    }, []);

    const handleCountdownComplete = useCallback(() => {
      stopCountdown();
      setNotificationState({ isInNotificationState: false });
      minimizeSession();
      handleCountdownCompleted();
    }, [stopCountdown, setNotificationState, minimizeSession, handleCountdownCompleted]);

    // Simplified event handlers - just update state, effects handle pause/resume
    const handleHoverExtension = useCallback(() => {
      setHovering(true);
    }, [setHovering]);

    const handleUnhoverExtension = useCallback(() => {
      setHovering(false);
    }, [setHovering]);

    const handleInputFocus = useCallback(() => {
      setInputFocused(true);
    }, [setInputFocused]);

    const handleInputBlur = useCallback(() => {
      setInputFocused(false);
    }, [setInputFocused]);

    const handleInputChange = useCallback(() => {
      // For typing, we want to focus the input if not already focused
      if (!state.ui.isInputFocused) {
        setInputFocused(true);
      }
    }, [state.ui.isInputFocused, setInputFocused]);

    useMDSHotKey(SESSION_CLOSE_ARTIFACT_HOTKEY, closeArtifact, {
      enabled: !!state.ui.activeArtifact,
    });

    const sessionViewMode = useMemo<SessionViewMode>(() => {
      if (state.ui.isInNotificationState) {
        return SessionViewMode.Notification;
      }

      if (state.ui.isMinimized) {
        return SessionViewMode.Minimized;
      }

      // Check if preview view is enabled
      if (state.ui.isPreview) {
        return SessionViewMode.Preview;
      }

      return SessionViewMode.Standard;
    }, [state.ui.isMinimized, state.ui.isInNotificationState, state.ui.isPreview]);

    // Track previous session view mode
    const prevSessionViewModeRef = useRef<SessionViewMode>(sessionViewMode);
    useEffect(() => {
      if (sessionViewMode !== prevSessionViewModeRef.current) {
        dispatch({
          type: MDSSessionUIAction.SetSessionPrevMode,
          payload: prevSessionViewModeRef.current,
        });
        prevSessionViewModeRef.current = sessionViewMode;
      }
    }, [sessionViewMode]);

    useEffect(() => {
      handleViewModeChanged({ sessionViewMode });
    }, [handleViewModeChanged, sessionViewMode]);

    const isExpanded = useMemo(() => {
      if (
        sessionViewMode === SessionViewMode.Standard ||
        sessionViewMode === SessionViewMode.Notification ||
        sessionViewMode === SessionViewMode.Preview
      ) {
        return true;
      }

      return false;
    }, [sessionViewMode]);

    const isMini = useMemo(() => {
      return (
        sessionViewMode === SessionViewMode.Notification ||
        sessionViewMode === SessionViewMode.Preview
      );
    }, [sessionViewMode]);

    const isSessionTitleVisible = isExpanded && sessionViewMode !== SessionViewMode.Notification;
    const isCountdownVisible = isMini && state.ui.isCountdownActive;
    const isInputFormVisible = isExpanded;
    const isSessionViewExpanded = isExpanded;

    const finalizedSessionItems = useMemo(
      () =>
        finalizeSessionItems({
          sessionItems: state.events.sessionItems,
          sessionStatus: state.events.sessionStatus,
          sessionError: state.ui.sessionError,
        }),
      [state.events.sessionItems, state.events.sessionStatus, state.ui.sessionError]
    );

    const contextValue: MDSSessionContextValue = {
      internalSessionItems: state.events.sessionItems,
      finalizedSessionItems,
      sessionViewMode,
      sessionPrevMode: state.ui.sessionPrevMode,
      sessionConfig: config,
      internalSessionEvents: state.events.sessionEvents,
      sessionStatus: state.events.sessionStatus,
      activeArtifact: state.ui.activeArtifact,
      countdownDurationMs: state.ui.countdownDurationMs,
      isCountdownPaused: state.ui.isCountdownPaused,
      isSessionTitleVisible,
      isCountdownVisible,
      isInputFormVisible,
      isSessionViewExpanded,
      sessionError: state.ui.sessionError,

      // Actions
      viewArtifact,
      closeArtifact,
      upsertSessionEvents,
      updateSessionStatus,
      minimizeSession,
      expandSession,
      expandFromNotification,
      toggleSessionItemExpandedState,
      startCountdown,
      stopCountdown,
      onCountdownComplete: handleCountdownComplete,
      handleHoverExtension,
      handleUnhoverExtension,
      handleInputFocus,
      handleInputBlur,
      handleInputChange,
      handleRetry,
      setSessionError,
      clearSessionError,
      setPreviewMode,
      expandFromPreview,
      setNotificationState,
      publishNotification,
      focusComboboxInput,
      setComboboxInputRef,
      setCurrentSessionId,
    };

    useImperativeHandle(ref, () => contextValue);

    return <MDSSessionContext.Provider value={contextValue}>{children}</MDSSessionContext.Provider>;
  }
);

export const useMDSSession = (): MDSSessionContextValue => {
  const context = useContext(MDSSessionContext);

  if (!context) {
    throw new Error("useMDSSession must be used within a MDSSessionProvider");
  }

  return context;
};

/**
 * Hook to check if a specific session feature is enabled
 */
export const useSessionFeature = (feature: SessionFeature): boolean => {
  const { sessionConfig } = useMDSSession();
  return isSessionFeatureEnabled(sessionConfig, feature);
};
